import { init, registerRemotes, loadRemote } from "@module-federation/runtime";

// ──────────────────────────────────────────────────────────────────────────────
// Types inlined here to avoid circular dependency between mfe-core and mfe-registry.
// These mirror the types in @repo/mfe-registry/types.ts.
// ──────────────────────────────────────────────────────────────────────────────

type ComponentLike = (props: Record<string, unknown>) => unknown;

export interface MfeModuleFederationConfig {
  scope: string;
  exposedModule: string;
  devUrl: string;
  prodUrl?: string;
  cssAssetPath?: string;
}

export interface MfeDefinition {
  name: string;
  packageName: string;
  routePrefix: string;
  singleSpa: { port: number; scriptUrl: string };
  moduleFederation: MfeModuleFederationConfig;
  source: "local" | "remote";
}

export interface MfeModule {
  default?: ComponentLike;
  App?: ComponentLike;
}

// ──────────────────────────────────────────────────────────────────────────────
// Public API
// ──────────────────────────────────────────────────────────────────────────────

export interface RuntimeLoaderOptions {
  /** Whether to use prod URLs. Determined by environment. */
  useProdUrls: boolean;
  /** Host application name for MF runtime registration. */
  hostName: string;
  /** React version for shared singleton config. Defaults to "18.2.0". */
  reactVersion?: string;
}

export interface RuntimeLoader {
  /**
   * Registers and loads an MFE by name.
   * On first call: registers the remote, then loads the exposed module.
   * On subsequent calls: returns cached module.
   */
  load(mfe: MfeDefinition): Promise<MfeModule>;

  /**
   * Returns true if the MFE has been previously loaded in this session.
   */
  isLoaded(mfeName: string): boolean;

  /**
   * Clears cached state for testing/hot-reload purposes.
   */
  reset(): void;
}

// ──────────────────────────────────────────────────────────────────────────────
// Implementation
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Creates a RuntimeLoader instance that manages on-demand MFE remote
 * registration and module loading with internal caching.
 */
export function createRuntimeLoader(
  options: RuntimeLoaderOptions,
): RuntimeLoader {
  // Whether init() has been called for the MF runtime
  let initialized = false;

  // Track which scopes have already been registered with the MF runtime
  const registeredScopes = new Set<string>();

  // Cache loaded modules keyed by MFE name
  const moduleCache = new Map<string, MfeModule>();

  // Track in-flight load promises to avoid duplicate concurrent requests
  const loadPromises = new Map<string, Promise<MfeModule>>();

  /**
   * Lazily initializes the Module Federation runtime on the first load() call.
   * Configures the host instance name and shared singleton declarations for
   * react and react-dom so that MFE remotes consume the host's copies.
   */
  function ensureInitialized(): void {
    if (initialized) return;

    const reactVersion = options.reactVersion ?? "18.2.0";

    init({
      name: options.hostName,
      remotes: [],
      shared: {
        react: {
          version: reactVersion,
          scope: "default",
          shareConfig: { singleton: true, requiredVersion: `^${reactVersion}` },
        },
        "react-dom": {
          version: reactVersion,
          scope: "default",
          shareConfig: { singleton: true, requiredVersion: `^${reactVersion}` },
        },
      },
    });

    initialized = true;
  }

  /**
   * Determines the entry URL for a given MFE based on the loader options.
   * Uses prodUrl when available and useProdUrls is true; otherwise uses devUrl.
   */
  function resolveEntryUrl(mfe: MfeDefinition): string {
    if (options.useProdUrls && mfe.moduleFederation.prodUrl) {
      return mfe.moduleFederation.prodUrl;
    }
    return mfe.moduleFederation.devUrl;
  }

  /**
   * Core load logic: initializes runtime if needed, registers the remote
   * if this scope hasn't been registered yet, then loads the module.
   */
  async function doLoad(mfe: MfeDefinition): Promise<MfeModule> {
    ensureInitialized();

    const { scope, exposedModule } = mfe.moduleFederation;
    const entry = resolveEntryUrl(mfe);

    // Register the remote if this scope hasn't been registered yet
    if (!registeredScopes.has(scope)) {
      registerRemotes([{ name: scope, entry, type: "module" }]);
      registeredScopes.add(scope);
    }

    // Build the remote specifier: "scope/ExposedModule"
    // exposedModule is typically "./App" — strip the leading "./" for loadRemote
    const modulePath = exposedModule.replace(/^\.\//, "");
    const remoteSpecifier = `${scope}/${modulePath}`;

    const loadedModule = await loadRemote(remoteSpecifier);

    return loadedModule as MfeModule;
  }

  function load(mfe: MfeDefinition): Promise<MfeModule> {
    const { name } = mfe;

    // Return cached module immediately
    if (moduleCache.has(name)) {
      return Promise.resolve(moduleCache.get(name)!);
    }

    // If there's already an in-flight request for this MFE, return that promise
    if (loadPromises.has(name)) {
      return loadPromises.get(name)!;
    }

    // Start loading and cache the promise to deduplicate concurrent calls
    const promise = doLoad(mfe)
      .then((mod) => {
        moduleCache.set(name, mod);
        loadPromises.delete(name);
        return mod;
      })
      .catch((error) => {
        loadPromises.delete(name);
        throw error;
      });

    loadPromises.set(name, promise);
    return promise;
  }

  function isLoaded(mfeName: string): boolean {
    return moduleCache.has(mfeName);
  }

  function reset(): void {
    registeredScopes.clear();
    moduleCache.clear();
    loadPromises.clear();
    initialized = false;
  }

  return { load, isLoaded, reset };
}
